import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  AfterViewInit,
  QueryList,
  ContentChildren,
  ChangeDetectorRef,
  ViewChildren
} from '@angular/core';
import { NG_VALUE_ACCESSOR, NG_VALIDATORS, AbstractControl } from '@angular/forms';
import { AxisChipComponent } from './chip.component';

@Component({
  selector: 'axis-chip-list',
  templateUrl: './chip-list.component.html',
  host: {
    '[class.axis-chip-wrapper]': 'true',
    '[attr.tabindex]': '-1',
    'role': 'listbox',
    '[attr.aria-label]': 'ariaLabel',
    '[attr.aria-labelledby]': 'ariaLabelledby',
    'aria-multiselectable': 'true',
    '(keydown)': 'handleKeyDown($event)',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AxisChipListComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AxisChipListComponent),
      multi: true
    }
  ]
})
export class AxisChipListComponent implements AfterViewInit {
  @Input('aria-orientation') ariaOrientation: string; // Horizontal, vertical
  @Input() ariaLabel = 'please select chips.';
  @Input() ariaLabelledby: string;
  @Input() compareWith: (o1: any, o2: any) => boolean;
  @Input() errorStateMatcher: any;
  @Input() multiple: boolean;
  @Input() selectable: boolean;
  @Input() dataProvider: any[];
  @Input() required = false;
  @Input() disabled = false;
  @Output() change: EventEmitter<any> = new EventEmitter();
  @Output() removed: EventEmitter<any> = new EventEmitter();
  public model: any = '';
  @ViewChildren(AxisChipComponent) axisChipListArray: QueryList<AxisChipComponent>;
  @ContentChildren(AxisChipComponent, {
    // We need to use `descendants: true`, because Ivy will no longer match
    // indirect descendants if it's left as false.
    descendants: true
  }) chips: QueryList<AxisChipComponent>;

  selectedList: any[] = [];
  errorState: boolean;
  focused: boolean;
  selected: any[] | any;
  constructor(private changeDetectorRef: ChangeDetectorRef) { }
  private propagateModelChange = (_: any) => { };
  private onTouched: any = (_: any) => { };

  ngAfterViewInit() {
    this.selectionChange();
  }

  private selectionChange() {
    this.chips.forEach(element => {
      if (element.selected) {
        this.onSelectionChange({ value: element.value, selected: element.selected });
      }
      element.selectionChange.subscribe((value) => {
        this.onSelectionChange(value);
      });
    });
  }

  onSelectionChange(data) {
    if (data.selected) {
      if (this.dataProvider && this.dataProvider.length) {
        const newData = this.dataProvider.filter(x => x.value === data.value)[0];
        if (newData) {
          newData.selected = true;
          this.selectedList.push(newData);
        } else {
          this.selectedList.push(data);
        }
      } else {
        this.selectedList.push(data);
      }
    } else {
      const filteredList = this.selectedList.filter(x => x.value === data.value);
      if (filteredList.length > 0) {
        const index = this.selectedList.indexOf(filteredList[0]);
        this.selectedList.splice(index, 1);
      }
    }
    this.change.emit(this.selectedList);
    this.model = this.selectedList;
    this.propagateModelChange(this.model);
    this.changeDetectorRef.detectChanges();
  }

  onRemove(event) {
    const items = this.dataProvider.filter(x=> x.value === event.value && x.heading === event.heading);
    if (items && items.length) {
      event = items[0];
    }
    this.removed.emit(event);
  }

  preDefinedSelection() {
    if (!this.dataProvider && !this.chips) {
      return;
    }
    if (this.chips) {
      this.preDefinedChipSelection();
    }
    if (!this.dataProvider) {
      return;
    }
    if (Array.isArray(this.model)) {
      this.selectedList = [];
      this.model.forEach(element => {
        const data = this.dataProvider.filter(x => x.value === element.value);
        if (data && data.length > 0) {
          data[0].selected = true;
          this.selectedList.push({
            value: data[0].value,
            selected: true
          });
        }
      });
    } else if (typeof this.model === 'number') {
      this.dataProvider[this.model].selected = true;
      this.selectedList.push(this.dataProvider[this.model]);
    } else if (typeof this.model === 'string') {
      const data = this.dataProvider.filter(x => x.value === this.model);
      if (data && data.length > 0) {
        data[0].selected = true;
        this.selectedList.push({
          value: data[0].value,
          selected: true
        });
      }
    } else {
      const data = this.dataProvider.filter(x => x.value === this.model.value);
      if (data && data.length > 0) {
        data[0].selected = true;
        this.selectedList.push({
          value: data[0].value,
          selected: true
        });
      }
    }
  }

  preDefinedChipSelection() {
    if (Array.isArray(this.model)) {
      this.selectedList = [];
      this.model.forEach(element => {
        const data = this.chips.filter(x => x.value === element.value);
        if (data && data.length > 0) {
          data[0].selected = true;
          this.selectedList.push({
            value: data[0].value,
            selected: true
          });
        }
      });
    } else if (typeof this.model === 'number') {
      this.chips[this.model].selected = true;
      this.selectedList.push({
        value: this.chips[this.model].value,
        selected: true
      });
    } else if (typeof this.model === 'string') {
      const data = this.chips.filter(x => x.value === this.model);
      if (data && data.length > 0) {
        data[0].selected = true;
        this.selectedList.push({
          value: data[0].value,
          selected: true
        });
      }
    } else {
      const data = this.chips.filter(x => x.value === this.model.value);
      if (data && data.length > 0) {
        data[0].selected = true;
        this.selectedList.push({
          value: data[0].value,
          selected: true
        });
      }
    }
  }

  resetSelection() {
    this.model = null;
  }

  // Implementing ControlValueAccessor

  writeValue(value: any) {
    if (value || value === 0) {
      this.model = value;
      this.preDefinedSelection();
    } else {
      this.model = null;
      this.resetSelection();
    }
  }

  registerOnChange(fn: any): void {
    this.propagateModelChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  validate(control: AbstractControl): { [key: string]: any } {
    if (this.required) {
      return this.selectedList && this.selectedList.length ? null : { axisChip: { valid: false } };
    }
  }

  // ControlValueAccessor ENDS

  add(data) {
    if (!this.dataProvider) {
      this.dataProvider = [];
    }
    if (Array.isArray(data)) {
      data.forEach(element => {
        this.dataProvider.push(element);
      });
    } else {
      this.dataProvider.push(data);
    }
  }

  removeAll() {
    if (this.chips && this.chips.length) {
      this.chips.forEach(chip => {
        chip.deSelect();
      });
    }
    if (this.axisChipListArray && this.axisChipListArray.length) {
      this.axisChipListArray.forEach(element => {
        element.deSelect();
      });
    }
  }
  handleKeyDown(event: KeyboardEvent): void {
    const keyCode = event.keyCode;
    switch (keyCode) {
      case 32:
      case 13:
        // Always prevent space from scrolling the page since the list has focus
        event.preventDefault();
        break;
      case 38:
      case 39:
        this.nextChip(event);
        event.preventDefault();
        break;
      case 37:
      case 40:
        this.previousChip(event);
        event.preventDefault();
        break;
    }
  }

  nextChip(event) {
    if (event.target
      && event.target.parentElement
      && event.target.parentElement['nextElementSibling']
      && event.target.parentElement['nextElementSibling'].children[0]) {
      const currentListItem = event.target.parentElement['nextElementSibling'].children[0];
      if (currentListItem && !currentListItem.hasAttribute('disabled')) {
        currentListItem.focus();
      } else if (currentListItem && currentListItem.hasAttribute('disabled')) {
        const newEvent = { target : currentListItem};
        this.nextChip(newEvent);
      }
    } else {
      this.firstChip();
    }
  }
  previousChip(event) {
    if (event.target
      && event.target.parentElement
      && event.target.parentElement['previousElementSibling']
      && event.target.parentElement['previousElementSibling'].children[0]) {
      const currentListItem = event.target.parentElement['previousElementSibling'].children[0];
      if (currentListItem && !currentListItem.hasAttribute('disabled')) {
        currentListItem.focus();
      } else if (currentListItem && currentListItem.hasAttribute('disabled')) {
        const newEvent = { target : currentListItem};
        this.previousChip(newEvent);
      }
    } else {
      this.lastChip();
    }
  }

  firstChip() {
    let element: any;
    if (this.chips.length) {
      element = this.chips.first._elementRef.nativeElement.children[0];
      if (element && !element.hasAttribute('disabled')) {
        element.focus();
      } else if (element && element.hasAttribute('disabled')) {
        const newEvent = { target : element};
        this.nextChip(newEvent);
      }
    }

    if (this.axisChipListArray.length) {
      element = this.axisChipListArray.first._elementRef.nativeElement.children[0];
      if (element && !element.hasAttribute('disabled')) {
        element.focus();
      } else if (element && element.hasAttribute('disabled')) {
        const newEvent = { target : element};
        this.nextChip(newEvent);
      }
    }
  }

  lastChip() {
    let element: any;
    if (this.chips.length) {
      element = this.chips.last._elementRef.nativeElement.children[0];
      if (element && !element.hasAttribute('disabled')) {
        element.focus();
      } else if (element && element.hasAttribute('disabled')) {
        const newEvent = { target : element};
        this.previousChip(newEvent);
      }
    }
    if (this.axisChipListArray.length) {
      element = this.axisChipListArray.last._elementRef.nativeElement.children[0];
      if (element && !element.hasAttribute('disabled')) {
        element.focus();
      } else if (element && element.hasAttribute('disabled')) {
        const newEvent = { target : element};
        this.previousChip(newEvent);
      }
    }
  }

}
